"use client";

import { MobileDrawer, BillingTokenStatusBanner } from "@/components";
import {
  IntroModal,
  type IntroRequirement,
} from "@/components/introModal/IntroModal";
import { useAppUI } from "@/context/AppUIContext";
import { useModal } from "@/hooks";
import { useApiReady } from "@/hooks/useApiReady";
import { usePermission } from "@/hooks/usePermission/usePermission";
import type { AppDispatch, RootState } from "@/store";
import { fetchEmployees } from "@/store/modules/employees/thunks";
import { fetchSubscriptionConfig } from "@/store/modules/Subscriptions/thunks";
import { LayoutProps } from "@/types";
import { usePathname } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Header, Sidebar } from "..";
import {
  applyPermissions,
  getMenuItemsByType,
  getMenuType,
} from "../sidebar/menuConfig";
import { MenuItemComponent } from "../sidebar/MenuItem";
import * as SidebarStyles from "../sidebar/styles";
import {
  getEstablishmentStatus,
  getInitialExpandedCategory,
  getLocaleFromPathname,
  getLocalizedPath,
  isCategoryExpanded,
  isEstablishmentActive,
  isPathActive,
} from "../sidebar/utils";
import * as S from "./styles";

const Layout: React.FC<LayoutProps> = ({ children, dict }) => {
  const { sidebarOpen, toggleSidebar } = useAppUI();
  const { isOpen: isOpenEditProfile, toggleModal: toggleEditProfileModal } =
    useModal();
  const pathname = usePathname();
  const { hasPermission, isSuperAdmin, session } = usePermission();
  const dispatch: AppDispatch = useDispatch();
  const { isReady: isApiReady } = useApiReady(250);

  const { employees } = useSelector((state: RootState) => state.employees);
  const subscriptionConfig = useSelector(
    (state: RootState) => state.subscriptionConfig,
  );

  const locale = useMemo(() => getLocaleFromPathname(pathname), [pathname]);

  const canReadEmployees = useMemo(
    () => hasPermission("read user"),
    [hasPermission],
  );
  const canReadSubscriptionConfig = useMemo(
    () => hasPermission("read subscription"),
    [hasPermission],
  );

  useEffect(() => {
    if (!isApiReady) return;

    const requests: Promise<unknown>[] = [];
    if (canReadEmployees) {
      requests.push(dispatch(fetchEmployees({ without_pagination: 1 } as any)));
    }
    if (canReadSubscriptionConfig) {
      requests.push(
        dispatch(
          fetchSubscriptionConfig({
            establishment: String((session?.user as any)?.merchantid ?? ""),
          }) as any,
        ),
      );
    }

    if (requests.length) {
      void Promise.allSettled(requests);
    }
  }, [dispatch, isApiReady, canReadEmployees, canReadSubscriptionConfig]);

  const missingRequirements: IntroRequirement[] = useMemo(() => {
    const missing: IntroRequirement[] = [];

    if (canReadEmployees) {
      const employeesList = Array.isArray(employees?.data)
        ? employees.data
        : [];
      const activeEmployees = employeesList.filter((e: any) => {
        if (!e || e.deleted_at != null) return false;
        const isVisible =
          e.show_in_schedule_and_site === 1 ||
          e.show_in_schedule_and_site === true ||
          e.show_in_schedule_and_site === "1";
        return isVisible;
      });

      if (activeEmployees.length === 0) {
        missing.push({
          name: "Funcionários",
          message: "Cadastre pelo menos um funcionário para começar.",
          redirect: "/register/employees",
        });
      } else {
        const employeesWithoutWorkHours = activeEmployees.filter((e: any) => {
          const workHours = e?.employee_work_hours;
          return !Array.isArray(workHours) || workHours.length === 0;
        });

        employeesWithoutWorkHours.forEach((e: any) => {
          missing.push({
            name: `Horários (${e?.name || "Funcionário"})`,
            message: `Configure o horário de trabalho de ${e?.name || "este funcionário"}.`,
            redirect: "/register/employees",
          });
        });
      }
    }

    if (canReadSubscriptionConfig) {
      const hasConfig =
        Boolean((subscriptionConfig as any)?.data?.data) ||
        Boolean((subscriptionConfig as any)?.data);

      if (!hasConfig) {
        missing.push({
          name: "Configuração de Comissão",
          message:
            "Configure a comissão do estabelecimento para finalizar a configuração inicial.",
          redirect: "/subscriptions/plans",
        });
      }
    }

    return missing;
  }, [
    employees?.data,
    subscriptionConfig?.data,
    session,
    canReadEmployees,
    canReadSubscriptionConfig,
  ]);

  const [isIntroOpen, setIsIntroOpen] = useState(false);

  useEffect(() => {
    if (missingRequirements.length > 0) {
      setIsIntroOpen(true);
    } else {
      setIsIntroOpen(false);
    }
  }, [pathname, missingRequirements.length]);

  const establishmentStatus = useMemo(
    () => getEstablishmentStatus(session),
    [session],
  );

  const isEstablishmentActiveStatus = useMemo(
    () => isEstablishmentActive(establishmentStatus),
    [establishmentStatus],
  );

  const menuType = useMemo(
    () => getMenuType(isSuperAdmin(), isEstablishmentActiveStatus),
    [isSuperAdmin, isEstablishmentActiveStatus],
  );

  const hasPermissionRef = useRef(hasPermission);
  hasPermissionRef.current = hasPermission;

  const hasPermissionStable = useCallback((permission: string) => {
    return hasPermissionRef.current(permission);
  }, []);

  const baseNavItems = useMemo(
    () =>
      getMenuItemsByType(menuType, {
        dict,
        hasPermission: hasPermissionStable,
      }),
    [menuType, dict, hasPermissionStable],
  );

  const navItems = useMemo(
    () => applyPermissions(baseNavItems, hasPermissionStable),
    [baseNavItems, hasPermissionStable],
  );

  const navItemsRef = useRef(navItems);
  const isActiveRef = useRef(
    useCallback(
      (path: string | undefined) => isPathActive(path, pathname, locale),
      [pathname, locale],
    ),
  );

  navItemsRef.current = navItems;

  const isActive = useCallback(
    (path: string | undefined) => isPathActive(path, pathname, locale),
    [pathname, locale],
  );

  isActiveRef.current = isActive;

  const getLocalizedPathMemo = useCallback(
    (path: string) => getLocalizedPath(path, locale),
    [locale],
  );

  const [expandedCategory, setExpandedCategory] = useState<string[]>([]);

  useEffect(() => {
    const newExpandedCategory = getInitialExpandedCategory(
      navItemsRef.current,
      isActiveRef.current,
    );
    setExpandedCategory((prev) => {
      if (prev.length !== newExpandedCategory.length) {
        return newExpandedCategory;
      }
      if (prev.length === 0 && newExpandedCategory.length === 0) {
        return prev;
      }
      if (prev[0] !== newExpandedCategory[0]) {
        return newExpandedCategory;
      }
      return prev;
    });
  }, [pathname]);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategory((prev) =>
      prev.includes(categoryId) ? [] : [categoryId],
    );
  }, []);

  const handleDrawerClose = () => {
    if (typeof window !== "undefined" && window.innerWidth < 992) {
      toggleSidebar();
    }
  };

  const handleNavigate = () => {
    if (typeof window !== "undefined" && window.innerWidth < 992) {
      if (sidebarOpen) {
        toggleSidebar();
      }
    }
  };

  const isSubscriptionsRoute = useMemo(() => {
    const normalized = pathname?.toLowerCase() ?? "";
    return normalized.includes("/subscriptions");
  }, [pathname]);

  return (
    <S.Layout $isOpen={sidebarOpen}>
      <Sidebar dict={dict} />
      <S.ContentLayout>
        <Header dict={dict} />
        <S.Content>
          {isSubscriptionsRoute && <BillingTokenStatusBanner />}
          {children}
        </S.Content>
      </S.ContentLayout>
      <MobileDrawer
        isOpen={sidebarOpen}
        onClose={handleDrawerClose}
        dict={dict}
        onOpenEditProfileModal={toggleEditProfileModal}
      >
        <SidebarStyles.ListNavigation>
          {navItems.map((item) => (
            <MenuItemComponent
              key={item.id}
              item={item}
              locale={locale}
              sidebarOpen={true}
              isActive={isActive}
              getLocalizedPath={getLocalizedPathMemo}
              isExpanded={isCategoryExpanded(
                item.id,
                expandedCategory,
                item,
                isActive,
              )}
              onToggle={toggleCategory}
              onNavigate={handleNavigate}
            />
          ))}
        </SidebarStyles.ListNavigation>
      </MobileDrawer>
    </S.Layout>
  );
};

export default Layout;
